import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatKisa, formatTl } from '../lib/api';
import Ipucu from '../ui/Ipucu';
import KpiKart, { KpiIzgara } from '../ui/KpiKart';
import { gunYaz, paraYaz } from './kasa/ortak';
import { NotSatiri } from './cek/parca';

/**
 * NAKİT AKIŞI (ileriye dönük) — bugünkü kasa + banka bakiyesinden başlayıp hafta hafta beklenen
 * giriş ve çıkışları, net ve yürüyen bakiyeyi gösterir (docs/CEK-SENET-KREDI.md §4).
 * Hesap sunucuda (`apps/api/src/nakit-akis.ts`); rolün göremediği kaynak akışa hiç girmez.
 */
type Kaynak = 'CEK_ALINAN' | 'SATIS_FATURASI' | 'SIPARIS' | 'CEK_VERILEN' | 'KREDI' | 'ALIS_FATURASI';
type Kalem = { kaynak: Kaynak; yon: 'GIRIS' | 'CIKIS'; vade: string; kurus: number; para: string; ad: string; belge: string | null; yol: string | null; gecmis: boolean; hafta: number };
type Hafta = { no: number; bas: string; bit: string; girisKurus: number; cikisKurus: number; netKurus: number; yuruyenKurus: number; eksi: boolean; kaynak: Partial<Record<Kaynak, number>> };
type Akis = {
  bugun: string; bit: string; gun: number; baslangicKurus: number; dovizBakiye: { para: string; kurus: number }[];
  gecmis: { girisKurus: number; cikisKurus: number; adet: number; dahil: boolean }; haftalar: Hafta[]; kalemler: Kalem[]; doviz: Kalem[];
  kaynaklar: { kod: Kaynak; ad: string; yon: 'GIRIS' | 'CIKIS'; dahil: boolean; adet: number; kurus: number }[];
  vadesiz: { adet: number; kurus: number }; toplamGirisKurus: number; toplamCikisKurus: number; sonBakiyeKurus: number;
  enDusuk: { no: number; bas: string; kurus: number } | null; eksiHaftaAdet: number;
};
const KAYNAK_KISA: Record<Kaynak, string> = { CEK_ALINAN: 'Alınan çek / senet', SATIS_FATURASI: 'Satış faturası', SIPARIS: 'Sipariş ödemesi', CEK_VERILEN: 'Verilen çek / senet', KREDI: 'Kredi taksiti', ALIS_FATURASI: 'Alış faturası' };
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const kisa = (g: string) => `${Number(g.slice(8, 10))} ${AY[Number(g.slice(5, 7)) - 1]}`;

export default function NakitAkis() {
  const [gun, setGun] = useState<'30' | '90' | '180'>('90');
  const [gecmisDahil, setGecmisDahil] = useState(false);
  const [veri, setVeri] = useState<Akis | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState<number | null>(null);

  useEffect(() => {
    api<Akis>(`/api/nakit-akis?gun=${gun}&gecmis=${gecmisDahil ? 'dahil' : 'haric'}`).then((r) => { setVeri(r); setHata(null); }).catch((e) => setHata((e as Error).message));
  }, [gun, gecmisDahil]);

  const haftaKalemleri = useMemo(() => {
    const m = new Map<number, Kalem[]>();
    for (const k of veri?.kalemler ?? []) m.set(k.hafta, [...(m.get(k.hafta) ?? []), k]);
    return m;
  }, [veri]);
  const dahilOlmayan = (veri?.kaynaklar ?? []).filter((k) => !k.dahil);

  const Dokum = ({ kalemler }: { kalemler: Kalem[] }) => (
    <table className="ccd-table ccd-akis-dokum">
      <tbody>
        {kalemler.map((k, i) => (
          <tr key={i} className="ccd-tr">
            <td className="ccd-td ccd-td-nowrap">{gunYaz(k.vade)}</td>
            <td className="ccd-td"><span className={`ccd-badge ${k.yon === 'GIRIS' ? 'ccd-badge-success' : 'ccd-badge-danger'}`}>{KAYNAK_KISA[k.kaynak]}</span></td>
            <td className="ccd-td">{k.ad}{k.belge && <span className="ccd-muted"> · {k.yol ? <Link to={k.yol}>{k.belge}</Link> : k.belge}</span>}</td>
            <td className={`ccd-td ccd-td-num ${k.yon === 'GIRIS' ? 'ccd-kasa-giris' : 'ccd-kasa-cikis'}`}>{k.yon === 'GIRIS' ? '+' : '−'}{paraYaz(k.kurus, k.para)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="ccd-title">Nakit akışı</h1>
          <div className="ccd-meta">Bugünkü kasa ve banka bakiyenizden başlayarak, önümüzdeki haftalarda beklenen para girişleri ve çıkışları.</div>
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="ccd-kasa-secim" role="group" aria-label="Kaç gün ileriye">
            {(['30', '90', '180'] as const).map((g) => <button key={g} type="button" aria-pressed={gun === g} onClick={() => setGun(g)}>{g} gün</button>)}
          </div>
          <Link className="ccd-btn ccd-btn-sm" to="/kasa">← Kasa ve Banka</Link>
        </div>
      </div>

      <NotSatiri>
        Bu bir <b>tahmindir</b>: kayıtlı çek, senet, fatura, sipariş ödemesi ve kredi taksitlerinin vadelerinde gerçekleşeceği varsayılır. Elden yapılan, kaydı olmayan giriş ve çıkışlar (maaş, kira, günlük giderler) burada yoktur. <Ipucu id="nakit-akisi" />
      </NotSatiri>
      {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      {!veri ? !hata && <div className="ccd-meta">Yükleniyor…</div> : (
        <>
          <KpiIzgara>
            <KpiKart ton="yesil" ikon="cuzdan" etiket="Bugünkü kasa + banka" fihrist="nakit-akisi-baslangic" deger={formatKisa(veri.baslangicKurus)} tam={formatTl(veri.baslangicKurus)} to="/kasa"
                     alt={<><div>Başlangıç bakiyesi (TL hesaplar)</div>{veri.dovizBakiye.length > 0 && <div>{veri.dovizBakiye.map((d) => paraYaz(d.kurus, d.para)).join(' · ')}</div>}</>} />
            <KpiKart ton="mavi" ikon="grafik" etiket={`Beklenen giriş (${veri.haftalar.length} hafta)`} fihrist="nakit-akisi-kalemler" deger={formatKisa(veri.toplamGirisKurus)} tam={formatTl(veri.toplamGirisKurus)} soluk={veri.toplamGirisKurus === 0}
                     alt={<div>Çek, senet, fatura ve sipariş ödemeleri</div>} />
            <KpiKart ton="turuncu" ikon="sepet" etiket={`Beklenen çıkış (${veri.haftalar.length} hafta)`} fihrist="nakit-akisi-kalemler" deger={formatKisa(veri.toplamCikisKurus)} tam={formatTl(veri.toplamCikisKurus)} soluk={veri.toplamCikisKurus === 0}
                     alt={<div>Verilen çekler, kredi taksitleri, alış faturaları</div>} />
            <KpiKart ton={veri.sonBakiyeKurus < 0 || veri.eksiHaftaAdet > 0 ? 'kirmizi' : 'mor'} ikon="terazi" etiket="Dönem sonu beklenen bakiye" fihrist="nakit-akisi-yuruyen" deger={formatKisa(veri.sonBakiyeKurus)} tam={formatTl(veri.sonBakiyeKurus)}
                     alt={<div>{veri.eksiHaftaAdet > 0 ? <b className="ccd-kasa-cikis">{veri.eksiHaftaAdet} hafta eksiye düşüyor</b> : veri.enDusuk ? `En düşük: ${formatTl(veri.enDusuk.kurus)} (${kisa(veri.enDusuk.bas)} haftası)` : ''}</div>} />
          </KpiIzgara>

          {veri.gecmis.adet > 0 && (
            <div className="ccd-akis-gecmis">
              <span><b>{veri.gecmis.adet} kalemin vadesi geçmiş</b>, hâlâ bekliyor: giriş <b className="ccd-kasa-giris">{formatTl(veri.gecmis.girisKurus)}</b> · çıkış <b className="ccd-kasa-cikis">{formatTl(veri.gecmis.cikisKurus)}</b>. <Ipucu id="nakit-akisi-gecmis" /></span>
              <label className="ccd-kasa-onay"><input type="checkbox" checked={gecmisDahil} onChange={(e) => setGecmisDahil(e.target.checked)} /><span>Bunları da bakiyeye kat (bugün gerçekleşecekmiş gibi)</span></label>
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setAcik(acik === -1 ? null : -1)}>{acik === -1 ? 'Dökümü gizle' : 'Dökümü göster'}</button>
              {acik === -1 && <Dokum kalemler={haftaKalemleri.get(-1) ?? []} />}
            </div>
          )}

          <div className="ccd-table-wrap ccd-kasa-tablo ccd-kasa-tablo-uzun">
            <table className="ccd-table ccd-akis-tablo">
              <thead><tr>
                <th className="ccd-th">Hafta</th>
                <th className="ccd-th ccd-td-num">Giriş <Ipucu id="nakit-akisi-kalemler" /></th>
                <th className="ccd-th ccd-td-num">Çıkış</th>
                <th className="ccd-th ccd-td-num">Net</th>
                <th className="ccd-th ccd-td-num">Hafta sonu bakiye <Ipucu id="nakit-akisi-yuruyen" /></th>
                <th className="ccd-th" />
              </tr></thead>
              <tbody>
                <tr className="ccd-tr ccd-kasa-acilis">
                  <td className="ccd-td" colSpan={4}>Başlangıç: bugünkü kasa + banka{veri.gecmis.dahil ? ' + vadesi geçmiş kalemlerin neti' : ''}</td>
                  <td className="ccd-td ccd-td-num"><b>{formatTl(veri.baslangicKurus + (veri.gecmis.dahil ? veri.gecmis.girisKurus - veri.gecmis.cikisKurus : 0))}</b></td><td className="ccd-td" />
                </tr>
                {veri.haftalar.map((h) => {
                  const ks = haftaKalemleri.get(h.no - 1) ?? [];
                  return (
                    <Fragment key={h.no}>
                      <tr className={`ccd-tr${h.eksi ? ' ccd-akis-eksi' : ''}`}>
                        <td className="ccd-td ccd-td-nowrap"><b>{h.no}. hafta</b><div className="ccd-meta">{kisa(h.bas)} – {kisa(h.bit)}</div></td>
                        <td className="ccd-td ccd-td-num ccd-kasa-giris">{h.girisKurus ? formatTl(h.girisKurus) : <span className="ccd-muted">—</span>}</td>
                        <td className="ccd-td ccd-td-num ccd-kasa-cikis">{h.cikisKurus ? formatTl(h.cikisKurus) : <span className="ccd-muted">—</span>}</td>
                        <td className={`ccd-td ccd-td-num${h.netKurus < 0 ? ' ccd-kasa-cikis' : ''}`}>{h.netKurus ? `${h.netKurus > 0 ? '+' : '−'}${formatTl(Math.abs(h.netKurus))}` : <span className="ccd-muted">—</span>}</td>
                        <td className="ccd-td ccd-td-num"><b className={h.eksi ? 'ccd-kasa-cikis' : undefined}>{h.eksi ? '−' : ''}{formatTl(Math.abs(h.yuruyenKurus))}</b>{h.eksi && <div className="ccd-meta ccd-kasa-cikis">eksiye düşüyor</div>}</td>
                        <td className="ccd-td">{ks.length > 0 && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" aria-expanded={acik === h.no} onClick={() => setAcik(acik === h.no ? null : h.no)}>{acik === h.no ? 'Gizle' : `${ks.length} kalem`}</button>}</td>
                      </tr>
                      {acik === h.no && <tr className="ccd-tr"><td className="ccd-td ccd-akis-dokum-hucre" colSpan={6}><Dokum kalemler={ks} /></td></tr>}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ccd-card ccd-stack-sm">
            <div className="ccd-fieldset-title">Kalemler nereden geliyor <Ipucu id="nakit-akisi-kalemler" /></div>
            <div className="ccd-akis-kaynaklar">
              {veri.kaynaklar.map((k) => (
                <div key={k.kod} className={`ccd-akis-kaynak${k.dahil ? '' : ' ccd-kasa-kart-pasif'}`}>
                  <span className="ccd-meta">{k.yon === 'GIRIS' ? 'Giriş' : 'Çıkış'} · {k.ad}</span>
                  <b className={k.yon === 'GIRIS' ? 'ccd-kasa-giris' : 'ccd-kasa-cikis'}>{k.dahil ? formatTl(k.kurus) : 'yetkiniz yok'}</b>
                  <span className="ccd-meta">{k.dahil ? `${k.adet} kalem` : 'akışa girmedi'}</span>
                </div>
              ))}
            </div>
            {dahilOlmayan.length > 0 && <div className="ccd-meta">Rolünüzün göremediği kaynaklar ({dahilOlmayan.map((k) => k.ad.toLocaleLowerCase('tr')).join(', ')}) bu tabloda yoktur; tablo eksik olabilir.</div>}
            {veri.vadesiz.adet > 0 && <div className="ccd-meta">Vadesi henüz belli olmayan {veri.vadesiz.adet} sipariş ödemesi ({formatTl(veri.vadesiz.kurus)}) tabloda yoktur: vadesi sevk ya da fatura gününe bağlı. <Ipucu id="odeme-plani" /></div>}
            {veri.doviz.length > 0 && (
              <div className="ccd-meta">Dövizli {veri.doviz.length} kalem TL'ye çevrilmedi, tabloya girmedi: {veri.doviz.slice(0, 6).map((k) => `${gunYaz(k.vade)} ${k.yon === 'GIRIS' ? '+' : '−'}${paraYaz(k.kurus, k.para)} (${k.ad})`).join(' · ')}{veri.doviz.length > 6 ? ' …' : ''}</div>
            )}
          </div>
        </>
      )}
    </>
  );
}
